import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Play } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Button } from "@/components/ui/button";

const HERO_EASE = [0.22, 1, 0.36, 1];

export default function Hero() {
  const reduceMotion = useReducedMotion();
  const item = reduceMotion
    ? {}
    : {
        hidden: { opacity: 0, y: 26 },
        visible: {
          opacity: 1,
          y: 0,
          transition: { duration: 0.72, ease: HERO_EASE },
        },
      };

  return (
    <section data-testid="hero-section" className="relative overflow-hidden border-b border-slate-200">
      <div className="absolute inset-0 bg-grid bg-grid-fade pointer-events-none" aria-hidden="true" />
      <div className="absolute -top-32 right-0 size-[520px] rounded-full bg-cyan-400/10 blur-3xl pointer-events-none" aria-hidden="true" />

      <div className="relative mx-auto max-w-7xl px-4 pb-20 pt-14 sm:px-6 sm:pt-20 sm:pb-24 lg:px-8 lg:pt-28 lg:pb-32">
        <motion.div
          className="grid items-center gap-10 lg:grid-cols-12 lg:gap-10"
          initial={reduceMotion ? false : "hidden"}
          animate={reduceMotion ? undefined : "visible"}
          variants={
            reduceMotion
              ? {}
              : {
                  hidden: {},
                  visible: {
                    transition: {
                      delayChildren: 0.08,
                      staggerChildren: 0.1,
                    },
                  },
                }
          }
        >
          <div className="lg:col-span-6">
            <motion.div variants={item} className="inline-flex max-w-full items-center gap-2 rounded-full border border-slate-200 bg-white/70 px-3 py-1.5 text-[10px] font-mono uppercase tracking-[0.18em] text-slate-600 backdrop-blur sm:text-[11px] sm:tracking-[0.2em]">
              <span className="size-1.5 rounded-full bg-cyan-500 pulse-dot" />
              Dienstleistung · Vehicle Data Infrastructure
            </motion.div>

            <motion.h1 variants={item} className="mt-6 font-display font-bold text-slate-900 text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              Wir machen aus{" "}
              <span className="text-gradient-accent">Fahrzeugdaten</span>{" "}
              einen sichtbaren Vertriebskanal.
            </motion.h1>

            <motion.p variants={item} className="mt-6 text-base sm:text-lg text-slate-600 leading-relaxed max-w-xl">
              Erfassen, bereinigen, anreichern. Wir bauen aus Ihrem Bestand strukturierte
              Feeds für Google Vehicle Ads und SEO-starke Fahrzeug-Landingpages — als Service,
              nicht als Tool.
            </motion.p>

            <motion.div variants={item} className="mt-9 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
              <Link to="/audit">
                <Button
                  data-testid="hero-audit-btn"
                  data-track="cta_click"
                  data-cta="audit"
                  data-location="hero"
                  className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-md bg-slate-900 pl-5 pr-4 text-sm text-white hover:bg-slate-800 sm:w-auto"
                >
                  Audit anfragen
                  <ArrowUpRight className="size-4" />
                </Button>
              </Link>
              <Link to="/demo">
                <Button
                  data-testid="hero-demo-btn"
                  data-track="cta_click"
                  data-cta="demo"
                  data-location="hero"
                  variant="outline"
                  className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-md border-slate-200 bg-white pl-4 pr-5 text-sm text-slate-900 hover:border-slate-300 sm:w-auto"
                >
                  <Play className="size-3.5 fill-slate-900" />
                  Demo ansehen
                </Button>
              </Link>
            </motion.div>

            <motion.dl variants={item} className="mt-10 grid max-w-md grid-cols-1 gap-4 sm:mt-12 sm:grid-cols-3 sm:gap-6">
              <Metric label="Datenpunkte / Fahrzeug" value="120+" />
              <Metric label="Feed-Score Ø" value="96%" />
              <Metric label="Sichtbarkeit Ø" value="+38%" />
            </motion.dl>
          </div>

          <motion.div
            variants={
              reduceMotion
                ? {}
                : {
                    hidden: { opacity: 0, scale: 0.97, y: 24 },
                    visible: {
                      opacity: 1,
                      scale: 1,
                      y: 0,
                      transition: { duration: 0.9, ease: HERO_EASE },
                    },
                  }
            }
            className="relative lg:col-span-6"
          >
            <HeroShowcase />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

function Metric({ label, value }) {
  return (
    <div className="border-l-2 border-cyan-500 pl-3">
      <p className="font-display text-2xl font-bold text-slate-900 tabular-nums">{value}</p>
      <p className="label-mono text-slate-500 mt-1">{label}</p>
    </div>
  );
}

function HeroShowcase() {
  const reduceMotion = useReducedMotion();

  return (
    <div className="relative">
      <div className="absolute -inset-6 bg-gradient-to-br from-cyan-100/50 via-transparent to-teal-50 rounded-3xl blur-2xl" aria-hidden="true" />

      {/* Main screenshot — LCP-Element: animiert weiterhin (scale/slide),
          aber startet mit voller opacity, damit framer-motion das LCP
          nicht um ~1,3 s verzögert. Nur transform ist GPU-beschleunigt. */}
      <motion.div
        initial={reduceMotion ? false : { scale: 0.965, y: 24 }}
        animate={reduceMotion ? undefined : { scale: 1, y: 0 }}
        transition={{ duration: 1.02, delay: 0.16, ease: HERO_EASE }}
        className="relative overflow-hidden rounded-xl border border-slate-200 bg-white ring-soft-lg"
      >
        <div className="flex h-9 items-center justify-between border-b border-slate-200 bg-slate-50/60 px-3 sm:px-4">
          <div className="flex items-center gap-1.5">
            <span className="size-2.5 rounded-full bg-slate-300" />
            <span className="size-2.5 rounded-full bg-slate-300" />
            <span className="size-2.5 rounded-full bg-slate-300" />
          </div>
          <div className="hidden font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400 sm:block">
            app.adsfux.io · anzeigen-simulator
          </div>
          <div className="w-6 sm:w-12" />
        </div>
        <img
          src="/screenshots/06_dienstleister_overview.webp"
          alt="adsFUX Anzeigen-Simulator — Live-Vorschau einer Google-Vehicle-Ad für BMW X5"
          className="w-full h-auto block"
          width="1600"
          height="1050"
          fetchpriority="high"
          decoding="async"
        />
      </motion.div>

      {/* Floating panel · Vehicle-Ads delta */}
      <motion.div
        initial={reduceMotion ? false : { opacity: 0, x: -116, y: 22, rotate: -1.8 }}
        animate={reduceMotion ? undefined : { opacity: 1, x: 0, y: 0, rotate: 0 }}
        transition={{ duration: 0.92, delay: 0.54, ease: HERO_EASE }}
        className="float-soft absolute -bottom-7 -left-8 hidden w-72 rounded-2xl border border-cyan-900/70 bg-[linear-gradient(180deg,rgba(13,19,31,0.98)_0%,rgba(8,13,24,0.98)_100%)] p-5 text-white shadow-[0_28px_90px_rgba(8,15,28,0.34)] ring-1 ring-cyan-400/8 md:block"
      >
        <div className="label-mono text-cyan-200/70">Google Vehicle Ads</div>
        <p className="mt-3 font-display text-[36px] font-bold tabular-nums text-white">+38.2%</p>
        <p className="mt-1 text-[11px] text-slate-400">Klick-Reichweite vs. Vormonat</p>
        <div className="mt-5 flex h-9 items-end gap-1.5">
          {[28, 34, 31, 42, 38, 49, 55, 51, 62, 58, 70].map((h, i) => (
            <div
              key={i}
              className="flex-1 rounded-md bg-gradient-to-t from-cyan-500/80 to-cyan-300"
              style={{ height: `${h}%` }}
            />
          ))}
        </div>
      </motion.div>

      {/* Floating panel · score */}
      <motion.div
        initial={reduceMotion ? false : { opacity: 0, x: 110, y: -24, rotate: 1.8 }}
        animate={reduceMotion ? undefined : { opacity: 1, x: 0, y: 0, rotate: 0 }}
        transition={{ duration: 0.92, delay: 0.68, ease: HERO_EASE }}
        className="float-soft absolute -right-7 top-8 hidden w-52 flex-col items-center rounded-2xl border border-cyan-900/70 bg-[linear-gradient(180deg,rgba(13,19,31,0.98)_0%,rgba(8,13,24,0.98)_100%)] p-5 text-white shadow-[0_28px_90px_rgba(8,15,28,0.34)] ring-1 ring-cyan-400/8 lg:flex"
        style={{ animationDelay: "1.2s" }}
      >
        <p className="label-mono text-cyan-200/70">Anzeigenqualität</p>
        <div className="relative mt-3 size-24">
          <svg viewBox="0 0 36 36" className="size-24 -rotate-90">
            <circle cx="18" cy="18" r="15" fill="none" stroke="rgba(71,85,105,0.55)" strokeWidth="3" />
            <circle cx="18" cy="18" r="15" fill="none" stroke="url(#hg)" strokeWidth="3" strokeDasharray="94 100" strokeLinecap="round" />
            <defs>
              <linearGradient id="hg" x1="0" x2="1">
                <stop offset="0%" stopColor="rgb(6 182 212)" />
                <stop offset="100%" stopColor="rgb(20 184 166)" />
              </linearGradient>
            </defs>
          </svg>
          <span className="absolute inset-0 flex items-center justify-center font-display font-bold text-[34px] text-white">94</span>
        </div>
        <p className="mt-4 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-1 text-[12px] font-mono uppercase tracking-[0.15em] text-emerald-300">
          Exzellent
        </p>
      </motion.div>
    </div>
  );
}
